import { useEffect, useState } from "react";
import { auth, provider, db } from "../firebase";
import { signInWithPopup, onAuthStateChanged } from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { isSuperAdminEmail } from "../utils/superAdminEmails";
import {
  isLegacyHost,
  isLegacyUrlExpired,
  NEW_URL,
  EXPIRES_ON,
} from "../components/UrlSunsetWarning";

function GoogleG() {
  return (
    <img
      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
      alt="Google Logo"
      className="w-5 h-5"
    />
  );
}

const AUTH_MESSAGE_KEY = "auth_message";
const SSO_REQUIRED = "SSO_REQUIRED";

export default function Login() {
  const [loading, setLoading] = useState(true); // firebase check
  const [loadingGoogle, setLoadingGoogle] = useState(false); // button state
  const [user, setUser] = useState(null);
  const [ssoRequiredMessage, setSsoRequiredMessage] = useState(false);

  const ADMIN_EMAIL = "";

  // Show message when user was redirected because SSO is required (e.g. signed in with password)
  useEffect(() => {
    const msg = localStorage.getItem(AUTH_MESSAGE_KEY);
    if (msg === SSO_REQUIRED) {
      setSsoRequiredMessage(true);
      localStorage.removeItem(AUTH_MESSAGE_KEY);
    }
  }, []); 

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userRef = doc(
          db,
          "workspaces",
          "ai-apps-dashboard",
          "users",
          currentUser.uid
        );
  
        const existingUser = await getDoc(userRef);
  
        let role = "user";
        // if (currentUser.email === ADMIN_EMAIL) {
        //   role = "admin";
        // } else if (existingUser.exists()) {
        //   role = existingUser.data().role || "user";
        // }
  
        // ✅ Ensure Firestore record exists/updates
        if(!existingUser){
          await setDoc(
          userRef,
          {
            name: currentUser.displayName,
            email: currentUser.email,
            photo: currentUser.photoURL,
            role,
            createdAt
              : serverTimestamp(),
            lastLogin: serverTimestamp(),
          },
          { merge: true }
        );
      }
  
        // save in localStorage
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();
        localStorage.setItem("user", JSON.stringify(userData));
  
        setUser(currentUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });
  
    return () => unsubscribe();
  }, []);
  

  const handleGoogleSignIn = async () => {
    try {
      setLoadingGoogle(true); 
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userRef = doc(
        db,
        "workspaces",
        "ai-apps-dashboard",
        "users",
        user.uid
      );

      const existingUser = await getDoc(userRef);

      let role = "user";
      if (isSuperAdminEmail(user.email)) {
        role = "super_admin";
      } else if (user.email === ADMIN_EMAIL) {
        role = "admin";
      } else if (existingUser.exists()) {
        const existingRole = String(existingUser.data().role || "user")
          .trim()
          .toLowerCase()
          .replace(/[\s-]+/g, "_");
        role = existingRole;
      }

      await setDoc(
        userRef,
        {
          name: user.displayName,
          email: user.email,
          photo: user.photoURL,
          role,
          createdAt: existingUser.exists()
            ? existingUser.data().createdAt
            : serverTimestamp(),
          lastLogin: serverTimestamp(),
        },
        { merge: true }
      );

      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      localStorage.setItem("user", JSON.stringify(userData));

      // if (role === "admin") {
      //   window.location.replace("/admin");
      // } else {
      //   window.location.replace("/");
      // }
    } catch (error) {
      console.error("Google login failed:", error.message);
      setLoadingGoogle(false); 
    }
  };

  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <p className="text-gray-700 dark:text-gray-300">Loading...</p>
      </div>
    );
  }

  
  return (
    <div className="flex flex-col h-screen bg-gray-100 dark:bg-gray-900">
      {isLegacyHost() && !isLegacyUrlExpired() && (
        <div className="px-4 py-2 bg-amber-400 text-black text-center text-sm font-semibold">
          This link expires on {EXPIRES_ON}. Please use{" "}
          <a href={NEW_URL} className="underline font-bold">
            {NEW_URL}
          </a>
        </div>
      )}
      <div className="flex items-center justify-center flex-1">
      <div className="order-2 mx-auto w-full max-w-md">
        <div className="rounded-2xl border border-slate-200 bg-white dark:bg-gray-800 p-6 shadow-xl">
          <div className="mb-6 text-center">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-gray-100">
              Sign in
            </h3>
            <p className="text-sm text-slate-500 dark:text-gray-400">
              Use your work Google account (SSO required)
            </p>
          </div>

          {ssoRequiredMessage && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
              Please sign in with Google. SSO is required for all users.
            </div>
          )}

          <button
            type="button"
            disabled={loadingGoogle}
            onClick={handleGoogleSignIn}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70"
          >
            <GoogleG />
            {loadingGoogle ? "Signing in..." : "Continue with Google"}
          </button>

          <p className="mt-6 text-center text-xs text-slate-500 dark:text-gray-400">
            By continuing, you agree to our Terms & Privacy Policy.
          </p>
        </div>

        <div className="mt-6 text-center">
          <a
            href="https://cynetcorp.com/"
            target="_blank"
            rel="noreferrer"
            className="text-sm text-blue-700 dark:text-blue-400 hover:underline"
          >
            Visit company site
          </a>
        </div>
      </div>
      </div>
    </div>
  );
}
